import {
  dateAndTimeToZonedDateTimeCurrentZone,
  ZonedDateTimeFactory,
  ZonedDateTimeFormatter,
} from '@csp/csp-common-date-util';
import {
  BioComplianceStatus,
  BioComplianceStatusValueType,
  BioSampleAliquotIn,
  BioSampleCodeFormatType,
  BioSampleCollectIn,
  BioSampleCommentIn,
  BioSampleCommentValueType,
  BioSampleContainerNumber,
  BioSampleDeliveryLab,
  BioSampleDeliveryLabReasonType,
  BioSampleDisplayContainerNumber,
  BioSampleIdCustomStatusIns,
  BioSampleIn,
  BioSampleProcessIn,
  BioSampleRefType,
  BioSampleShipIn,
  BioSampleStatusType,
  CollectionStatusValueType,
  ProcessingStatusValue,
  ProcessingStatusValueType,
  ShippingNumber,
  ShippingStatusValueType,
} from '@csp/csp-common-hbs-model';
import { CustomStatusValueIn, IsoDateStr, Maybe, TimeStr, ZonedDateTimeStr } from '@csp/csp-common-model';
import { isDefined } from '@csp/csp-common-util';
import { BioSampleInV1, ContainerNumberInV1, CustomStatusInV1, CustomStatusValueInV1 } from '@csp/dmdp-api-hbs-dto';
import { BioSampleCodeFormatterUtil } from '../util/BioSampleCodeFormatterUtil';

const toBioSampleCustomStatusInV1 = (
  type: BioSampleStatusType,
  { value, reasonText, timestamp }: CustomStatusValueIn,
): CustomStatusInV1 => {
  const reasonTextToUse = reasonText?.trim();

  return {
    type,
    value,
    reason: reasonTextToUse?.length ? reasonTextToUse : undefined,
    localTimestamp: ZonedDateTimeFormatter.toZonedDateTimeString(timestamp),
  };
};

const fromValueToStorageComplianceStatusInV1 = ({
  currentStatus,
  value,
}: {
  currentStatus: BioComplianceStatus;
  value: BioComplianceStatusValueType;
}): CustomStatusInV1 =>
  toBioSampleCustomStatusInV1(BioSampleStatusType.STORAGE_COMPLIANCE, { ...currentStatus, value });

const fromValueToProcessComplianceStatusInV1 = ({
  currentStatus,
  value,
}: {
  currentStatus: BioComplianceStatus;
  value: BioComplianceStatusValueType;
}): CustomStatusInV1 =>
  toBioSampleCustomStatusInV1(BioSampleStatusType.PROCESS_COMPLIANCE, { ...currentStatus, value });

const fromValueToShippingNumberStatusInV1 = ({
  currentStatus,
  value,
}: {
  currentStatus: CustomStatusValueIn;
  value: ShippingNumber;
}): CustomStatusInV1 => toBioSampleCustomStatusInV1(BioSampleStatusType.SHIPPING_NUMBER, { ...currentStatus, value });

const fromValueAndReasonToDeliveryLabStatusInV1 = ({
  currentStatus,
  value,
  reasonText,
}: {
  currentStatus: CustomStatusValueIn;
  value: BioSampleDeliveryLab;
  reasonText: BioSampleDeliveryLabReasonType;
}): CustomStatusInV1 =>
  toBioSampleCustomStatusInV1(BioSampleStatusType.DELIVERY_LAB, {
    ...currentStatus,
    value,
    reasonText,
  });

const fromDateToShippingStatusInV1 = ({
  currentStatus,
  date,
}: {
  currentStatus: CustomStatusValueIn;
  date: IsoDateStr;
}): CustomStatusInV1 =>
  fromDateTimeOrDefaultToCustomStatusInV1({
    type: BioSampleStatusType.SHIPPING,
    currentStatus,
    dateIn: date,
    timeIn: '00:00',
  });

const toZonedDateTimeStrOrUndefined = (dateIn: IsoDateStr, timeIn: TimeStr): Maybe<ZonedDateTimeStr> => {
  const zonedDateTime = dateAndTimeToZonedDateTimeCurrentZone(dateIn, timeIn);
  return ZonedDateTimeFormatter.toZonedDateTimeString(zonedDateTime);
};

const fromDateTimeOrDefaultToCustomStatusInV1 = ({
  type,
  currentStatus,
  dateIn,
  timeIn,
}: {
  type: BioSampleStatusType;
  currentStatus: CustomStatusValueIn;
  dateIn: IsoDateStr;
  timeIn: TimeStr;
}): CustomStatusInV1 => ({
  type,
  value: currentStatus.value,
  localTimestamp: toZonedDateTimeStrOrUndefined(dateIn, timeIn),
  reason: currentStatus.reasonText,
});

const fromDateTimeToCollectionStatusInV1 = ({
  collectionStatus,
  dateIn,
  timeIn,
}: {
  collectionStatus: CustomStatusValueIn<CollectionStatusValueType>;
  dateIn: IsoDateStr;
  timeIn: TimeStr;
}): CustomStatusInV1 =>
  fromDateTimeOrDefaultToCustomStatusInV1({
    type: BioSampleStatusType.COLLECTION,
    currentStatus: collectionStatus,
    dateIn,
    timeIn,
  });

const fromDateTimeToInProcessStatusInV1 = ({
  currentStatus,
  dateIn,
  timeIn,
}: {
  currentStatus: ProcessingStatusValue;
  dateIn: IsoDateStr;
  timeIn: TimeStr;
}): CustomStatusInV1 =>
  fromDateTimeOrDefaultToCustomStatusInV1({
    type: BioSampleStatusType.PROCESSING,
    currentStatus: { ...currentStatus, value: ProcessingStatusValueType.IN_PROCESS },
    dateIn,
    timeIn,
  });

const fromDateTimeToProcessedStatusInV1 = ({
  currentStatus,
  dateIn,
  timeIn,
}: {
  currentStatus: ProcessingStatusValue;
  dateIn: IsoDateStr;
  timeIn: TimeStr;
}): CustomStatusInV1 =>
  fromDateTimeOrDefaultToCustomStatusInV1({
    type: BioSampleStatusType.PROCESSING,
    currentStatus: { ...currentStatus, value: ProcessingStatusValueType.PROCESSED },
    dateIn,
    timeIn,
  });

const toCollectCustomStatusV1s = ({
  collectionStatus,
  storageComplianceStatus,
  sampleType,
  analyteType,
}: BioSampleCollectIn): CustomStatusInV1[] => [
  toBioSampleCustomStatusInV1(BioSampleStatusType.COLLECTION, collectionStatus),
  toBioSampleCustomStatusInV1(BioSampleStatusType.STORAGE_COMPLIANCE, storageComplianceStatus),
  toBioSampleCustomStatusInV1(BioSampleStatusType.SAMPLE_TYPE, sampleType),
  toBioSampleCustomStatusInV1(BioSampleStatusType.ANALYTE_TYPE, analyteType),
];

const toAliquotCustomStatusV1s = ({
  collectionStatus,
  processComplianceStatus,
  processingStatus,
  storageComplianceStatus,
  sampleType,
  processSampleType,
  analyteType,
}: BioSampleAliquotIn): CustomStatusInV1[] =>
  [
    toBioSampleCustomStatusInV1(BioSampleStatusType.COLLECTION, collectionStatus),
    processingStatus.startedAt
      ? toBioSampleCustomStatusInV1(BioSampleStatusType.PROCESSING, {
          value: ProcessingStatusValueType.IN_PROCESS,
          timestamp: processingStatus.startedAt,
          reasonText: processingStatus.evaluatedStatusValue.reasonText,
        })
      : undefined,
    processingStatus.endedAt
      ? toBioSampleCustomStatusInV1(BioSampleStatusType.PROCESSING, {
          value: ProcessingStatusValueType.PROCESSED,
          timestamp: processingStatus.endedAt,
          reasonText: processingStatus.evaluatedStatusValue.reasonText,
        })
      : undefined,
    toBioSampleCustomStatusInV1(BioSampleStatusType.PROCESS_COMPLIANCE, processComplianceStatus),
    toBioSampleCustomStatusInV1(BioSampleStatusType.STORAGE_COMPLIANCE, storageComplianceStatus),
    toBioSampleCustomStatusInV1(BioSampleStatusType.PROCESSED_SAMPLE_TYPE, processSampleType),
    toBioSampleCustomStatusInV1(BioSampleStatusType.SAMPLE_TYPE, sampleType),
    toBioSampleCustomStatusInV1(BioSampleStatusType.ANALYTE_TYPE, analyteType),
  ].filter(isDefined);

const fromCollectInToBioSampleInV1 = (sampleIn: BioSampleCollectIn): BioSampleInV1 => ({
  customStatuses: toCollectCustomStatusV1s(sampleIn),
  ...mapSampleCodeAndPatientAndVisit(sampleIn),
});

const fromAliquotInToBioSampleInV1 = (sampleIn: BioSampleAliquotIn): BioSampleInV1 => {
  const inV1 = mapSampleCodeAndPatientAndVisit(sampleIn);
  return {
    ...inV1,
    customStatuses: toAliquotCustomStatusV1s(sampleIn),
    refs: [...(inV1.refs ?? []), { key: BioSampleRefType.ALIQUOT_PARENT_ID, value: sampleIn.parentSampleId }],
  };
};

const mapSampleCodeAndPatientAndVisit = (sampleIn: BioSampleIn): BioSampleInV1 => ({
  sampleCode: sampleIn.sampleCode,
  sampleKitCode: sampleIn.kitCode,
  sampleContainerNumber: sampleIn.containerNumber
    ? BioSampleCodeFormatterUtil.toBioSampleContainerNumber(sampleIn.containerNumber, [sampleIn.format])
    : undefined,
  sampleCodeFormat: sampleIn.format,
  kitType: sampleIn.kitType,
  kitDescription: sampleIn.kitDescription,
  kitExpirationDate: sampleIn.kitExpiryDate,
  containerType: sampleIn.containerType,
  containerDescription: sampleIn.containerDescription,
  userId: sampleIn.patientId,
  refs: [{ key: BioSampleRefType.VISIT_ID, value: sampleIn.visitId }],
  scheduleRequestRef: {
    requestCode: sampleIn.visitRequestRef.requestCode,
    versionCode: sampleIn.visitRequestRef.versionCode,
    scheduleCode: sampleIn.visitRequestRef.scheduleCode,
    repeatIndex: sampleIn.visitRequestRef.repeatIndex,
  },
});

const fromProcessInToBioSampleInV1 = ({
  sampleId,
  processingStatuses,
  processComplianceStatus,
  storageComplianceStatus,
}: BioSampleProcessIn): BioSampleIdCustomStatusIns => ({
  sampleId,
  customStatusInV1s: {
    customStatuses: [
      ...processingStatuses
        .filter(isDefined)
        .map(value => toBioSampleCustomStatusInV1(BioSampleStatusType.PROCESSING, value)),
      processComplianceStatus
        ? toBioSampleCustomStatusInV1(BioSampleStatusType.PROCESS_COMPLIANCE, processComplianceStatus)
        : undefined,
      storageComplianceStatus
        ? toBioSampleCustomStatusInV1(BioSampleStatusType.STORAGE_COMPLIANCE, storageComplianceStatus)
        : undefined,
    ].filter(isDefined),
  },
});

const fromShipInToBioSampleInV1 = ({
  sampleId,
  shippingStatus,
  shippingNumber,
  deliveryLab,
}: BioSampleShipIn): BioSampleIdCustomStatusIns => {
  const customStatusInV1s = [
    toBioSampleCustomStatusInV1(BioSampleStatusType.SHIPPING, shippingStatus),
    shippingNumber ? toBioSampleCustomStatusInV1(BioSampleStatusType.SHIPPING_NUMBER, shippingNumber) : undefined,
    deliveryLab ? toBioSampleCustomStatusInV1(BioSampleStatusType.DELIVERY_LAB, deliveryLab) : undefined,
  ].filter(isDefined);

  return {
    sampleId,
    customStatusInV1s: {
      customStatuses: customStatusInV1s,
    },
  };
};

const toUsedBioSampleCustomStatusInV1 = ({ comment }: BioSampleCommentIn): CustomStatusValueInV1 => {
  const reasonTextToUse = comment?.trim();
  return {
    value: ShippingStatusValueType.USED,
    localTimestamp: ZonedDateTimeFormatter.toZonedDateTimeString(ZonedDateTimeFactory.now()),
    reason: reasonTextToUse?.length ? reasonTextToUse : undefined,
  };
};

const fromCommentInToBioSampleInV1 = ({ comment }: BioSampleCommentIn): CustomStatusValueInV1 => ({
  value: BioSampleCommentValueType.ADDITIONAL,
  localTimestamp: ZonedDateTimeFormatter.toZonedDateTimeString(ZonedDateTimeFactory.now()),
  reason: comment,
});

const toContainerNumberInV1 = (
  format: BioSampleCodeFormatType,
  containerNumber: BioSampleDisplayContainerNumber | BioSampleContainerNumber,
): ContainerNumberInV1 => ({
  sampleContainerNumber: BioSampleCodeFormatterUtil.toBioSampleContainerNumber(containerNumber, [format]),
});

export const BioSampleInMapper = {
  fromCollectInToBioSampleInV1,
  fromAliquotInToBioSampleInV1,
  fromProcessInToBioSampleInV1,
  fromShipInToBioSampleInV1,
  fromCommentInToBioSampleInV1,
  toUsedBioSampleCustomStatusInV1,
  fromDateTimeToCollectionStatusInV1,
  toBioSampleCustomStatusInV1,
  toContainerNumberInV1,
  fromValueToStorageComplianceStatusInV1,
  fromValueToProcessComplianceStatusInV1,
  fromValueToShippingNumberStatusInV1,
  fromValueAndReasonToDeliveryLabStatusInV1,
  fromDateToShippingStatusInV1,
  fromDateTimeToInProcessStatusInV1,
  fromDateTimeToProcessedStatusInV1,
};
